import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  writeBatch
} from 'firebase/firestore';
import { db } from './config';

// Collection names
const COLLECTIONS = {
  PARKING_OWNERS: 'parkingOwners',
  USERS: 'users',
  PARKING_SPACES: 'spaces',
  BOOKINGS: 'bookings',
  KYC_DOCUMENTS: 'kycDocuments',
  PAYMENT_SETTINGS: 'paymentSettings',
  DEALS: 'deals',
  PASSES: 'pass',
  MEAL_DESK_COMPANIES: 'MealDeskCompanies',
  MEAL_DESK_VENDORS: 'MealDeskVendors',
  MEAL_DESK_VENDOR_SCHEDULE: 'MealDeskVendorSchedule',
  FACILITIES: 'facilities'
};

// Parking Owners Services
export const parkingOwnerService = {
  // Get all parking owners
  async getAllOwners() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.PARKING_OWNERS));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching parking owners:', error);
      throw error;
    }
  },

  // Get parking owner by ID
  async getOwnerById(ownerId) {
    try {
      const docRef = doc(db, COLLECTIONS.PARKING_OWNERS, ownerId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Owner not found');
      }
    } catch (error) {
      console.error('Error fetching owner:', error);
      throw error;
    }
  },

  // Filter parking owners
  async filterOwners(filters) {
    try {
      let q = collection(db, COLLECTIONS.PARKING_OWNERS);
      const constraints = [];

      if (filters.city) {
        constraints.push(where('city', '==', filters.city));
      }
      if (filters.state && filters.state !== 'All States') {
        constraints.push(where('state', '==', filters.state));
      }
      if (filters.phone) {
        constraints.push(where('phoneNumber', '>=', filters.phone));
      }

      const querySnapshot = await getDocs(query(q, ...constraints));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error filtering owners:', error);
      throw error;
    }
  },

  // Add new parking owner
  async addOwner(ownerData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.PARKING_OWNERS), ownerData);
      return docRef.id;
    } catch (error) {
      console.error('Error adding owner:', error);
      throw error;
    }
  },

  // Update parking owner
  async updateOwner(ownerId, updateData) {
    try {
      const docRef = doc(db, COLLECTIONS.PARKING_OWNERS, ownerId);
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating owner:', error);
      throw error;
    }
  },

  // Delete parking owner
  async deleteOwner(ownerId) {
    try {
      const docRef = doc(db, COLLECTIONS.PARKING_OWNERS, ownerId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting owner:', error);
      throw error;
    }
  },

  // Update parking fee
  async updateParkingFee(ownerId, fee) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, ownerId);
      await updateDoc(docRef, { userfee: parseFloat(fee) });
    } catch (error) {
      console.error('Error updating parking handling fee percentage:', error);
      throw error;
    }
  }
};

// Users Services
export const userService = {
  // Get all users
  async getAllUsers() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  // Get user by ID
  async getUserById(userId) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('User not found');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  // Add new user
  async addUser(userData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.USERS), userData);
      return docRef.id;
    } catch (error) {
      console.error('Error adding user:', error);
      throw error;
    }
  },

  // Update user
  async updateUser(userId, updateData) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  // Delete user
  async deleteUser(userId) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  // Update user askReferral field
  async updateUserAskReferral(userId) {
    try {
      console.log(`Starting updateUserAskReferral for userId: ${userId}`);
      const docRef = doc(db, COLLECTIONS.USERS, userId);
      console.log(`Document reference created:`, docRef);
      
      // First, check if the document exists and what its current data is
      const currentDoc = await getDoc(docRef);
      if (!currentDoc.exists()) {
        console.error(`Document ${userId} does not exist in ${COLLECTIONS.USERS} collection`);
        throw new Error(`User document ${userId} not found`);
      }
      
      const currentData = currentDoc.data();
      console.log(`Current document data:`, currentData);
      console.log(`Current askReferral value:`, currentData.askReferral);
      
      // Add additional fields to track referral process
      const updateData = { 
        askReferral: Boolean(true),
        referralRequestedAt: new Date(),
        referralStatus: 'requested', // New field to track status
        lastReferralUpdate: new Date() // Timestamp for tracking
      };
      console.log(`Update data:`, updateData);
      
      await updateDoc(docRef, updateData);
      console.log(`Successfully updated askReferral field for user ${userId}`);
      
      // No delay needed - Cloud Function checks if flag has changed
      
      // Verify the update by reading the document back
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        const data = updatedDoc.data();
        console.log(`Verification - Document data after update:`, data);
        console.log(`askReferral value:`, data.askReferral);
        console.log(`referralRequestedAt value:`, data.referralRequestedAt);
        console.log(`referralStatus value:`, data.referralStatus);
      }
    } catch (error) {
      console.error('Error updating user askReferral field:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  }
};

// Parking Spaces Services
export const parkingSpaceService = {
  // Get all parking spaces
  async getAllSpaces() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.PARKING_SPACES));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching all parking spaces:', error);
      throw error;
    }
  },

  // Get spaces by owner ID
  async getSpacesByOwnerId(ownerId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.PARKING_SPACES),
        where('ownerId', '==', ownerId)
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching parking spaces:', error);
      throw error;
    }
  },

  // Add new parking space
  async addSpace(spaceData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.PARKING_SPACES), spaceData);
      return docRef.id;
    } catch (error) {
      console.error('Error adding parking space:', error);
      throw error;
    }
  },


};

// Bookings Services
export const bookingService = {
  // Get active bookings by owner ID
  async getActiveBookingsByOwnerId(ownerId) {
    try {
      const q = query(
        collection(db, COLLECTIONS.BOOKINGS),
        where('ownerId', '==', ownerId),
        where('status', '==', 'active'),
        orderBy('date', 'desc')
      );
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching active bookings:', error);
      throw error;
    }
  }
};

// Dashboard Statistics Service
export const dashboardService = {
  // Get dashboard statistics
  async getDashboardStats() {
    try {
      const stats = {
        parkingOwners: 0,
        parkingUsers: 0,
        reviews: 0,
        invoices: 0,
        currentReservations: 0,
        futureReservations: 0,
        pastReservations: 0,
        totalEarnings: 0,
        disabledSpaces: 0,
        freeParking: 0,
        parkingSpaces: 0,
        totalPasses: 0,
        multiUsePasses: 0,
        oneTimePasses: 0,
        dealVouchers: 0,
        dealCoupons: 0,
        partners: 0,
        dealProviders: 0,
        dealSettlements: 0,
        mealVendors: 0,
        mealCustomers: 0,
        mealSettlements: 0,
        totalReservations: 0,
        confirmedReservations: 0,
        cancelledReservations: 0,
        // New reservation status counts
        reservedReservations: 0,
        checkinReservations: 0,
        finishedReservations: 0,
        canceledReservations: 0
      };

      // Get all users for both owners and users count
      const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
      
      // Get parking owners count - users with at least 1 space
      const usersWithSpaces = usersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        return userData.spacecount && userData.spacecount >= 1;
      });
      stats.parkingOwners = usersWithSpaces.length;

      // Get parking users count
      stats.parkingUsers = usersSnapshot.size;

      // Get partners count - users with type "Partner"
      const partnersSnapshot = usersSnapshot.docs.filter(doc => {
        const userData = doc.data();
        return userData.type === "Partner";
      });
      stats.partners = partnersSnapshot.length;

      // Get parking spaces count and calculate approved/disabled counts
      const spacesSnapshot = await getDocs(collection(db, COLLECTIONS.PARKING_SPACES));
      stats.parkingSpaces = spacesSnapshot.size;
      
      // Calculate free and disabled spaces counts
      let freeCount = 0;
      let disabledCount = 0;
      
      spacesSnapshot.forEach(doc => {
        const spaceData = doc.data();
        if (spaceData.Free === true) {
          freeCount++;
        }
        // Count spaces that are not approved (awaiting approval)
        if (spaceData.approved === false || spaceData.approved === undefined || spaceData.approved === null) {
          disabledCount++;
        }
      });
      
      stats.freeParking = freeCount;
      stats.disabledSpaces = disabledCount;

      // Get reservations count and statistics
      try {
        const reservationsSnapshot = await getDocs(collection(db, 'reservations'));
        stats.totalReservations = reservationsSnapshot.size;
        
        let confirmedCount = 0;
        let reservedCount = 0;
        let checkinCount = 0;
        let finishedCount = 0;
        let canceledCount = 0;
        let totalEarnings = 0;
        
        // Calculate total earnings from transactions collection instead of reservations
        try {
          const transactionsSnapshot = await getDocs(collection(db, 'transaction'));
          transactionsSnapshot.forEach(doc => {
            const transaction = doc.data();
            // Sum all transaction.total values
            const totalValue = parseFloat(transaction.total) || 0;
            totalEarnings += totalValue;
          });
        } catch (error) {
          console.error('Error fetching transactions for earnings calculation:', error);
        }
        
        reservationsSnapshot.forEach(doc => {
          const reservation = doc.data();
          // Keep reservation counting logic but don't use for earnings
          
          const state = reservation.state?.toLowerCase();
          
          if (state === 'confirmed') {
            confirmedCount++;
          } else if (state === 'reserved') {
            reservedCount++;
          } else if (state === 'checkin') {
            checkinCount++;
          } else if (state === 'finished') {
            finishedCount++;
          } else if (state === 'canceled' || state === 'cancelled') {
            canceledCount++;
          }
        });
        
        stats.confirmedReservations = confirmedCount;
        stats.cancelledReservations = canceledCount; // Keep for backward compatibility
        stats.reservedReservations = reservedCount;
        stats.checkinReservations = checkinCount;
        stats.finishedReservations = finishedCount;
        stats.canceledReservations = canceledCount;
        stats.totalEarnings = totalEarnings;
        stats.currentReservations = checkinCount; // Current reservations are checked in ones
      } catch (error) {
        console.error('Error fetching reservations:', error);
      }

      // Get active bookings count (keeping for backward compatibility)
      // const activeBookingsQuery = query(
      //   collection(db, COLLECTIONS.BOOKINGS),
      //   where('status', '==', 'active')
      // );
      // const activeBookingsSnapshot = await getDocs(activeBookingsQuery);
      // stats.currentReservations = activeBookingsSnapshot.size; // Commented out as we're using reservations now

      // Calculate total earnings from active bookings (keeping for backward compatibility)
      // let totalEarnings = 0;
      // activeBookingsSnapshot.forEach(doc => {
      //   const booking = doc.data();
      //   totalEarnings += booking.paidAmount || 0;
      // });
      // stats.totalEarnings = totalEarnings;

      // Get passes count and categorize by type
      try {
        const passesSnapshot = await getDocs(collection(db, COLLECTIONS.PASSES));
        let multiUseCount = 0;
        let oneTimeCount = 0;
        let dealVoucherCount = 0;
        let dealCouponCount = 0;
        
        passesSnapshot.forEach(doc => {
          const pass = doc.data();
          console.log('Pass data:', pass);
          
          // Count deal vouchers and coupons separately
          if (pass.type === 'Deal-Voucher') {
            dealVoucherCount++;
            console.log('Categorized as deal voucher');
            return;
          }
          
          if (pass.type === 'Deal-Coupon') {
            dealCouponCount++;
            console.log('Categorized as deal coupon');
            return;
          }
          
          // Check if it's a multi-use pass based on the 'multiple' field
          if (pass.multiple === true) {
            multiUseCount++;
            console.log('Categorized as multi-use pass');
          } else {
            // If multiple is not true, it's a one-time pass
            oneTimeCount++;
            console.log('Categorized as one-time pass');
          }
        });
        
        stats.multiUsePasses = multiUseCount;
        stats.oneTimePasses = oneTimeCount;
        stats.totalPasses = multiUseCount + oneTimeCount;
        stats.dealVouchers = dealVoucherCount;
        stats.dealCoupons = dealCouponCount;
        
        console.log('Pass counts - Multi-use:', multiUseCount, 'One-time:', oneTimeCount, 'Total:', stats.totalPasses, 'Deal Vouchers:', dealVoucherCount, 'Deal Coupons:', dealCouponCount);
      } catch (error) {
        console.error('Error fetching passes:', error);
        // Keep default values if there's an error
      }

      // Get KerbletMeal counts
      try {
        // Get meal customers count
        const mealCustomersSnapshot = await getDocs(collection(db, COLLECTIONS.MEAL_DESK_COMPANIES));
        stats.mealCustomers = mealCustomersSnapshot.size;

        // Get meal vendors count
        const mealVendorsSnapshot = await getDocs(collection(db, COLLECTIONS.MEAL_DESK_VENDORS));
        stats.mealVendors = mealVendorsSnapshot.size;

        console.log('KerbletMeal counts - Customers:', stats.mealCustomers, 'Vendors:', stats.mealVendors);
      } catch (error) {
        console.error('Error fetching KerbletMeal counts:', error);
        // Keep default values if there's an error
      }

      return stats;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  }
};

// Partners Services
export const partnerService = {
  // Get all partners
  async getAllPartners() {
    try {
      const q = query(collection(db, COLLECTIONS.USERS), where('type', '==', 'Partner'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching partners:', error);
      throw error;
    }
  },

  // Get partner by ID
  async getPartnerById(partnerId) {
    try {
      const docRef = doc(db, COLLECTIONS.USERS, partnerId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Partner not found');
      }
    } catch (error) {
      console.error('Error fetching partner:', error);
      throw error;
    }
  },

  // Get unique sales person emails from deals
  async getUniqueSalesPersonEmails() {
    try {
      console.log('Fetching deals from collection:', COLLECTIONS.DEALS);
      const dealsSnapshot = await getDocs(collection(db, COLLECTIONS.DEALS));
      console.log('Total deals found:', dealsSnapshot.docs.length);
      
      const salesPersonEmails = new Set();
      
      dealsSnapshot.docs.forEach((doc, index) => {
        const deal = doc.data();
        console.log(`Deal ${index + 1}:`, deal);
        console.log(`Deal ${index + 1} dealSalesPerson:`, deal.dealSalesPerson);
        
        if (deal.dealSalesPerson && deal.dealSalesPerson.trim()) {
          salesPersonEmails.add(deal.dealSalesPerson);
          console.log('Added sales person email:', deal.dealSalesPerson);
        }
      });
      
      const result = Array.from(salesPersonEmails).sort();
      console.log('Final unique sales person emails:', result);
      return result;
    } catch (error) {
      console.error('Error fetching unique sales person emails:', error);
      return [];
    }
  },

  // Filter partners
  async filterPartners(filters) {
    try {
      console.log('Filtering partners with filters:', filters);
      
      let q = query(collection(db, COLLECTIONS.USERS), where('type', '==', 'Partner'));
      const constraints = [];

      if (filters.city) {
        constraints.push(where('city', '==', filters.city));
      }
      if (filters.state && filters.state !== 'All States') {
        constraints.push(where('state', '==', filters.state));
      }
      if (filters.phone) {
        constraints.push(where('phone', '>=', filters.phone));
      }

      console.log('Query constraints:', constraints);
      const querySnapshot = await getDocs(query(q, ...constraints));
      const partners = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Partners after filter:', partners.length);
      return partners;
    } catch (error) {
      console.error('Error filtering partners:', error);
      throw error;
    }
  },

  // Update partner SendReminder field
  async updatePartnerSendReminder(partnerId) {
    try {
      console.log(`Starting updatePartnerSendReminder for partnerId: ${partnerId}`);
      const docRef = doc(db, COLLECTIONS.USERS, partnerId);
      console.log(`Document reference created:`, docRef);
      
      // First, check if the document exists and what its current data is
      const currentDoc = await getDoc(docRef);
      if (!currentDoc.exists()) {
        console.error(`Document ${partnerId} does not exist in ${COLLECTIONS.USERS} collection`);
        throw new Error(`Partner document ${partnerId} not found`);
      }
      
      const currentData = currentDoc.data();
      console.log(`Current document data:`, currentData);
      console.log(`Current SendReminder value:`, currentData.SendReminder);
      
      const updateData = { 
        SendReminder: true,
        lastReminderSent: new Date(),
        LastWhatsAppReminderSend: new Date()
      };
      console.log(`Update data:`, updateData);
      
      await updateDoc(docRef, updateData);
      console.log(`Successfully updated SendReminder field for partner ${partnerId}`);
      
      // Verify the update by reading the document back
      const updatedDoc = await getDoc(docRef);
      if (updatedDoc.exists()) {
        const data = updatedDoc.data();
        console.log(`Verification - Document data after update:`, data);
        console.log(`SendReminder value:`, data.SendReminder);
        console.log(`LastWhatsAppReminderSend value:`, data.LastWhatsAppReminderSend);
      }
    } catch (error) {
      console.error('Error updating partner SendReminder field:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  }
};

// Field Day Planner Services
export const fieldDayPlannerService = {
  // Get all partners from dealpromaster collection
  async getAllPartners() {
    try {
      const querySnapshot = await getDocs(collection(db, 'dealpromaster'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(partner => 
        partner.dealProMasterLatitude && 
        partner.dealProMasterLongitude &&
        partner.dealProMasterName
      );
    } catch (error) {
      console.error('Error fetching partners:', error);
      throw error;
    }
  },

  // Filter partners by location and category
  async filterPartners(filters) {
    try {
      let q = collection(db, 'dealpromaster');
      const constraints = [];

      if (filters.category) {
        constraints.push(where('dealProMasterCategory', '==', filters.category));
      }
      if (filters.city) {
        constraints.push(where('dealProMasterCity', '==', filters.city));
      }
      if (filters.state) {
        constraints.push(where('dealProMasterState', '==', filters.state));
      }

      const querySnapshot = await getDocs(query(q, ...constraints));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(partner => 
        partner.dealProMasterLatitude && 
        partner.dealProMasterLongitude &&
        partner.dealProMasterName
      );
    } catch (error) {
      console.error('Error filtering partners:', error);
      throw error;
    }
  },

  // Get unique categories for filtering
  async getUniqueCategories() {
    try {
      const querySnapshot = await getDocs(collection(db, 'dealpromaster'));
      const categories = new Set();
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.dealProMasterCategory) {
          categories.add(data.dealProMasterCategory);
        }
      });
      
      return Array.from(categories).sort();
    } catch (error) {
      console.error('Error fetching categories:', error);
      return [];
    }
  },

  // Get unique cities for filtering
  async getUniqueCities() {
    try {
      const querySnapshot = await getDocs(collection(db, 'dealpromaster'));
      const cities = new Set();
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.dealProMasterCity) {
          cities.add(data.dealProMasterCity);
        }
      });
      
      return Array.from(cities).sort();
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  },

  // Get unique states for filtering
  async getUniqueStates() {
    try {
      const querySnapshot = await getDocs(collection(db, 'dealpromaster'));
      const states = new Set();
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.dealProMasterState) {
          states.add(data.dealProMasterState);
        }
      });
      
      return Array.from(states).sort();
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  },

  // Clean data for Firestore (remove undefined values)
  cleanDataForFirestore(data) {
    if (typeof data !== 'object' || data === null) {
      return data === undefined ? null : data;
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.cleanDataForFirestore(item));
    }
    
    const cleaned = {};
    Object.keys(data).forEach(key => {
      const value = data[key];
      if (value !== undefined) {
        cleaned[key] = this.cleanDataForFirestore(value);
      }
    });
    
    return cleaned;
  },

  // Save field day plan to database
  async savePlan(planData) {
    try {
      // Clean the data before saving to Firestore
      const cleanedData = this.cleanDataForFirestore(planData);
      const docRef = await addDoc(collection(db, 'fieldDayPlans'), cleanedData);
      return docRef.id;
    } catch (error) {
      console.error('Error saving field day plan:', error);
      throw error;
    }
  },

  // Get all field day plans
  async getAllPlans() {
    try {
      const querySnapshot = await getDocs(collection(db, 'fieldDayPlans'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching field day plans:', error);
      throw error;
    }
  },

  // Get field day plan by ID
  async getPlanById(planId) {
    try {
      const docRef = doc(db, 'fieldDayPlans', planId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Plan not found');
      }
    } catch (error) {
      console.error('Error fetching field day plan:', error);
      throw error;
    }
  },

  // Send plan via email with detailed partner information
  async sendPlanEmail(planId, salesPersonEmail, emailData = null) {
    try {
      console.log(`Sending plan ${planId} to ${salesPersonEmail}`);
      
      let planData;
      if (emailData) {
        // Use provided email data with detailed partner information
        planData = emailData;
      } else {
        // Fallback to getting plan from database
        const plan = await this.getPlanById(planId);
        // Convert database plan to email format
        planData = {
          planDetails: {
            name: plan.name,
            startLocation: plan.startLocation,
            endLocation: plan.endLocation,
            waypoints: plan.waypoints,
            radius: plan.radius,
            totalPartners: plan.partners.length,
            totalDistance: plan.route.distance,
            totalDuration: plan.route.duration,
            categories: plan.selectedCategories,
            dataSource: plan.dataSource
          },
          partnerDetails: plan.partners.map(partner => ({
            name: partner.dealProMasterName || 'Unknown',
            category: partner.dealProMasterCategory || 'N/A',
            address: partner.dealProMasterAddressFull || 'N/A',
            city: partner.dealProMasterCity || 'N/A',
            state: partner.dealProMasterState || 'N/A',
            phone: partner.dealProMasterPhoneNumber || 'N/A',
            rating: partner.dealProMasterGoogleRating || 0,
            reviews: partner.dealProMasterGoogleReviewsCount || 0,
            website: partner.dealProMasterWebsite || '',
            googleMapsLink: partner.googleMapsLink || '',
            googlePlacesLink: partner.googlePlacesLink || '',
            visitStatus: partner.visitStatus || 'not_visited',
            notes: partner.notes || ''
          })),
          statistics: plan.partnerStats || {
            totalPartners: plan.partners.length,
            byCategory: {},
            withPhoneNumbers: 0,
            withRatings: 0,
            averageRating: 0
          }
        };
      }

      // Import and use the email service
      const { emailService } = await import('../services/emailService.js');
      const result = await emailService.sendFieldDayPlanEmail(salesPersonEmail, planData);
      
      return result;
    } catch (error) {
      console.error('Error sending plan email:', error);
      throw error;
    }
  }
};

// Get count of pending reviews
export async function getPendingReviewsCount() {
  try {
    const q = query(collection(db, 'reviews'), where('approved', '==', false));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Error fetching pending reviews count:', error);
    return 0;
  }
}

// Invoice Services
export const invoiceService = {
  // Get all invoices from receipts collection
  async getAllInvoices() {
    try {
      const querySnapshot = await getDocs(collection(db, 'receipts'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  },

  // Get invoice by ID
  async getInvoiceById(invoiceId) {
    try {
      const docRef = doc(db, 'receipts', invoiceId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Invoice not found');
      }
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  },

  // Get unpaid invoices count
  async getUnpaidInvoicesCount() {
    try {
      const q = query(collection(db, 'receipts'), where('paymentStatus', '==', 'notpaid'));
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error fetching unpaid invoices count:', error);
      return 0;
    }
  },

  // Get unpaid invoices count and total amount
  async getUnpaidInvoicesData() {
    try {
      const q = query(collection(db, 'receipts'), where('paymentStatus', '==', 'notpaid'));
      const snapshot = await getDocs(q);
      const totalAmount = snapshot.docs.reduce((sum, doc) => {
        const earning = parseFloat(doc.data().earning) || 0;
        return sum + earning;
      }, 0);
      return {
        count: snapshot.size,
        totalAmount: totalAmount
      };
    } catch (error) {
      console.error('Error fetching unpaid invoices data:', error);
      return { count: 0, totalAmount: 0 };
    }
  },

  // Filter invoices
  async filterInvoices(filters) {
    try {
      let q = collection(db, 'receipts');
      const constraints = [];

      if (filters.invoiceNumber) {
        constraints.push(where('invoiceNumber', '>=', filters.invoiceNumber));
      }
      if (filters.ownerMobile) {
        constraints.push(where('ownerMobile', '>=', filters.ownerMobile));
      }
      if (filters.paymentStatus && filters.paymentStatus !== 'All') {
        constraints.push(where('paymentStatus', '==', filters.paymentStatus));
      }

      const querySnapshot = await getDocs(query(q, ...constraints));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error filtering invoices:', error);
      throw error;
    }
  },

  // Update invoice payment status
  async updatePaymentStatus(invoiceId, paymentStatus) {
    try {
      const docRef = doc(db, 'receipts', invoiceId);
      await updateDoc(docRef, { 
        paymentStatus: paymentStatus,
        payDate: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating payment status:', error);
      throw error;
    }
  }
}; 

// Auth Services
export const authService = {
  // Find missing Auth users
  async findMissingAuthUsers(progressCallback = () => {}) {
    try {
      progressCallback(10);
      
      // Get all users from Firestore collection
      const usersSnapshot = await getDocs(collection(db, COLLECTIONS.USERS));
      const firestoreUIDs = new Set(usersSnapshot.docs.map(doc => doc.id));
      
      console.log(`Total Users Collection Docs: ${firestoreUIDs.size}`);
      
      progressCallback(30);
      
      // Simulate the Auth user listing process
      // In a real implementation, you would need a backend API endpoint that:
      // 1. Uses Firebase Admin SDK to list all Auth users
      // 2. Compares UIDs between Auth and Firestore
      // 3. Returns Auth users whose UIDs are not in Firestore
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 1500));
      progressCallback(60);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      progressCallback(100);
      
      // For demonstration purposes, create some fake missing users
      // In reality, these would be actual Auth users from the backend
      const missingUsers = {
        phone: [],
        email: []
      };
      
      // Simulate some missing Auth users
      const totalAuthUsers = firestoreUIDs.size + Math.floor(Math.random() * 5);
      
      // Create some fake phone-based users (Auth users not in Firestore)
      for (let i = 0; i < Math.floor(Math.random() * 3); i++) {
        missingUsers.phone.push({
          uid: `missing-phone-${i + 1}`,
          phone: `+91${Math.floor(Math.random() * 9000000000) + 1000000000}`,
          email: '',
          displayName: `Missing Phone User ${i + 1}`,
          createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
        });
      }
      
      // Create some fake email-based users (Auth users not in Firestore)
      for (let i = 0; i < Math.floor(Math.random() * 2); i++) {
        missingUsers.email.push({
          uid: `missing-email-${i + 1}`,
          phone: '',
          email: `missing${i + 1}@example.com`,
          displayName: `Missing Email User ${i + 1}`,
          createdAt: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000).toISOString()
        });
      }
      
      console.log(`Total Auth Users Missing in Users Collection: ${missingUsers.phone.length + missingUsers.email.length}`);
      
      return {
        totalAuthUsers,
        totalUsersCollection: firestoreUIDs.size,
        missingUsers
      };
    } catch (error) {
      console.error('Error finding missing auth users:', error);
      throw error;
    }
  },

  // Bulk delete missing Auth users
  async bulkDeleteMissingAuthUsers(userIds, progressCallback = () => {}) {
    try {
      progressCallback(10);
      
      // Simulate the process for demonstration
      // In a real implementation, you would need a backend API endpoint that:
      // 1. Uses Firebase Admin SDK to delete users in batches
      // 2. Handles errors gracefully
      // 3. Returns operation results
      
      // Simulate processing time
      await new Promise(resolve => setTimeout(resolve, 2000));
      progressCallback(60);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      progressCallback(80);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      progressCallback(100);
      
      return {
        totalAuthUsers: 0, // This would be the actual count
        totalUsersCollection: 0, // This would be the actual count
        deletedUsers: userIds.length
      };
    } catch (error) {
      console.error('Error in bulk delete missing auth users:', error);
      throw error;
    }
  },

  // Delete single Auth user
  async deleteSingleAuthUser(uid) {
    try {
      // Simulate the process for demonstration
      // In a real implementation, you would need a backend API endpoint that:
      // 1. Uses Firebase Admin SDK to delete the specific user
      // 2. Handles errors gracefully
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return { success: true };
    } catch (error) {
      console.error('Error deleting single auth user:', error);
      throw error;
    }
  }
};

// MealDesk Companies Services
export const mealDeskCompaniesService = {
  // Get all companies
  async getAllCompanies() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.MEAL_DESK_COMPANIES));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching companies:', error);
      throw error;
    }
  },

  // Get company by ID
  async getCompanyById(companyId) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_COMPANIES, companyId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Company not found');
      }
    } catch (error) {
      console.error('Error fetching company:', error);
      throw error;
    }
  },

  // Add new company
  async addCompany(companyData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.MEAL_DESK_COMPANIES), {
        ...companyData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding company:', error);
      throw error;
    }
  },

  // Update company
  async updateCompany(companyId, updateData) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_COMPANIES, companyId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating company:', error);
      throw error;
    }
  },

  // Delete company
  async deleteCompany(companyId) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_COMPANIES, companyId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting company:', error);
      throw error;
    }
  }
};

// MealDesk Vendors Services
export const mealDeskVendorsService = {
  // Get all vendors
  async getAllVendors() {
    try {
      console.log('Fetching all vendors from collection:', COLLECTIONS.MEAL_DESK_VENDORS);
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.MEAL_DESK_VENDORS));
      console.log('Found', querySnapshot.docs.length, 'vendors');
      
      const vendors = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('Vendors data:', vendors);
      return vendors;
    } catch (error) {
      console.error('Error fetching vendors:', error);
      throw error;
    }
  },

  // Get vendor by ID
  async getVendorById(vendorId) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_VENDORS, vendorId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Vendor not found');
      }
    } catch (error) {
      console.error('Error fetching vendor:', error);
      throw error;
    }
  },

  // Add new vendor
  async addVendor(vendorData) {
    try {
      console.log('Adding vendor to collection:', COLLECTIONS.MEAL_DESK_VENDORS);
      console.log('Vendor data:', vendorData);
      
      // Get current user from managers collection
      let createdBy = 'admin'; // fallback
      try {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.firebaseUid) {
          // Get manager details from managers collection
          const managerDoc = await getDoc(doc(db, 'managers', currentUser.firebaseUid));
          if (managerDoc.exists()) {
            const managerData = managerDoc.data();
            createdBy = managerData.email || managerData.name || currentUser.firebaseUid;
          } else {
            createdBy = currentUser.firebaseUid;
          }
        }
      } catch (error) {
        console.error('Error getting current user:', error);
        createdBy = 'admin';
      }
      
      const docRef = await addDoc(collection(db, COLLECTIONS.MEAL_DESK_VENDORS), {
        ...vendorData,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: createdBy,
        updatedBy: createdBy
      });
      
      console.log('Vendor added successfully with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error adding vendor:', error);
      throw error;
    }
  },

  // Update vendor
  async updateVendor(vendorId, updateData) {
    try {
      // Get current user from managers collection
      let updatedBy = 'admin'; // fallback
      try {
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.firebaseUid) {
          // Get manager details from managers collection
          const managerDoc = await getDoc(doc(db, 'managers', currentUser.firebaseUid));
          if (managerDoc.exists()) {
            const managerData = managerDoc.data();
            updatedBy = managerData.email || managerData.name || currentUser.firebaseUid;
          } else {
            updatedBy = currentUser.firebaseUid;
          }
        }
      } catch (error) {
        console.error('Error getting current user:', error);
        updatedBy = 'admin';
      }
      
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_VENDORS, vendorId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date(),
        updatedBy: updatedBy
      });
    } catch (error) {
      console.error('Error updating vendor:', error);
      throw error;
    }
  },

  // Delete vendor
  async deleteVendor(vendorId) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_VENDORS, vendorId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting vendor:', error);
      throw error;
    }
  }
};

// Reservation Services
export const reservationService = {
  // Get all reservations
  async getAllReservations() {
    try {
      const querySnapshot = await getDocs(collection(db, 'reservations'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching reservations:', error);
      throw error;
    }
  },

  // Get reservation by ID
  async getReservationById(reservationId) {
    try {
      const docRef = doc(db, 'reservations', reservationId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Reservation not found');
      }
    } catch (error) {
      console.error('Error fetching reservation:', error);
      throw error;
    }
  },

  // Filter reservations
  async filterReservations(filters) {
    try {
      let q = collection(db, 'reservations');
      const constraints = [];

      if (filters.state) {
        constraints.push(where('state', '==', filters.state));
      }
      if (filters.spaceId) {
        constraints.push(where('spotId', '==', filters.spaceId));
      }
      if (filters.startDate) {
        constraints.push(where('createTime', '>=', new Date(filters.startDate)));
      }
      if (filters.endDate) {
        constraints.push(where('createTime', '<=', new Date(filters.endDate)));
      }

      if (constraints.length > 0) {
        q = query(q, ...constraints, orderBy('createTime', 'desc'));
      } else {
        q = query(q, orderBy('createTime', 'desc'));
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error filtering reservations:', error);
      throw error;
    }
  },

  // Update reservation status
  async updateReservationStatus(reservationId, status, reason = null) {
    try {
      const docRef = doc(db, 'reservations', reservationId);
      const updateData = { 
        state: status,
        updatedAt: new Date()
      };
      
      if (reason) {
        updateData.reason = reason;
      }
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating reservation status:', error);
      throw error;
    }
  }
};

// MealDesk Vendor Schedule Services
export const mealDeskVendorScheduleService = {
  // Get all schedules
  async getAllSchedules() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.MEAL_DESK_VENDOR_SCHEDULE));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching schedules:', error);
      throw error;
    }
  },

  // Get schedule by ID
  async getScheduleById(scheduleId) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_VENDOR_SCHEDULE, scheduleId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Schedule not found');
      }
    } catch (error) {
      console.error('Error fetching schedule:', error);
      throw error;
    }
  },

  // Add new schedule
  async addSchedule(scheduleData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.MEAL_DESK_VENDOR_SCHEDULE), {
        ...scheduleData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding schedule:', error);
      throw error;
    }
  },

  // Update schedule
  async updateSchedule(scheduleId, updateData) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_VENDOR_SCHEDULE, scheduleId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  },

  // Delete schedule
  async deleteSchedule(scheduleId) {
    try {
      const docRef = doc(db, COLLECTIONS.MEAL_DESK_VENDOR_SCHEDULE, scheduleId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  },

  // Add bulk schedules
  async addBulkSchedules(schedulesData) {
    try {
      const batch = writeBatch(db);
      
      schedulesData.forEach(scheduleData => {
        const docRef = doc(collection(db, COLLECTIONS.MEAL_DESK_VENDOR_SCHEDULE));
        batch.set(docRef, {
          ...scheduleData,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      });
      
      await batch.commit();
    } catch (error) {
      console.error('Error adding bulk schedules:', error);
      throw error;
    }
  }
};

// Cities Services
export const citiesService = {
  // Get all cities
  async getAllCities() {
    try {
      const querySnapshot = await getDocs(collection(db, 'Cities'));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw error;
    }
  },

  // Get city by ID
  async getCityById(cityId) {
    try {
      const docRef = doc(db, 'Cities', cityId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('City not found');
      }
    } catch (error) {
      console.error('Error fetching city:', error);
      throw error;
    }
  },

  // Add new city
  async addCity(cityData) {
    try {
      const docRef = await addDoc(collection(db, 'Cities'), {
        ...cityData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding city:', error);
      throw error;
    }
  },

  // Update city
  async updateCity(cityId, updateData) {
    try {
      const docRef = doc(db, 'Cities', cityId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating city:', error);
      throw error;
    }
  },

  // Delete city
  async deleteCity(cityId) {
    try {
      const docRef = doc(db, 'Cities', cityId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting city:', error);
      throw error;
    }
  },

  // Get cities by state
  async getCitiesByState(state) {
    try {
      const q = query(collection(db, 'Cities'), where('State', '==', state));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching cities by state:', error);
      throw error;
    }
  },

  // Get unique states
  async getUniqueStates() {
    try {
      const querySnapshot = await getDocs(collection(db, 'Cities'));
      const states = new Set();
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.State) {
          states.add(data.State);
        }
      });
      
      return Array.from(states).sort();
    } catch (error) {
      console.error('Error fetching unique states:', error);
      throw error;
    }
  }
};

// Facilities Services
export const facilitiesService = {
  // Get all facilities
  async getAllFacilities() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.FACILITIES));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching facilities:', error);
      throw error;
    }
  },

  // Get facility by ID
  async getFacilityById(facilityId) {
    try {
      const docRef = doc(db, COLLECTIONS.FACILITIES, facilityId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      } else {
        throw new Error('Facility not found');
      }
    } catch (error) {
      console.error('Error fetching facility:', error);
      throw error;
    }
  },

  // Add new facility
  async addFacility(facilityData) {
    try {
      const docRef = await addDoc(collection(db, COLLECTIONS.FACILITIES), {
        ...facilityData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error adding facility:', error);
      throw error;
    }
  },

  // Update facility
  async updateFacility(facilityId, facilityData) {
    try {
      const docRef = doc(db, COLLECTIONS.FACILITIES, facilityId);
      await updateDoc(docRef, {
        ...facilityData,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error('Error updating facility:', error);
      throw error;
    }
  },

  // Delete facility
  async deleteFacility(facilityId) {
    try {
      const docRef = doc(db, COLLECTIONS.FACILITIES, facilityId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting facility:', error);
      throw error;
    }
  }
};

